import { Fragment, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FACILITIES_SHOWN_FIRST, useClientFacilityPages } from './useClientFacilityPages.js'
import { useDebouncedValue } from '../../roster/index.js'
import type { AdminClientSummary, ClientPurge } from '../index.js'
import type { AdminFacility } from '../../facilities/index.js'
import type { FacilityAdministrator } from '../../accounts/index.js'
import { Link, Skeleton, useFeatures } from '../../core/index.js'
import { EditIcon, FacilityIcon, MailIcon, PhoneIcon, WorkersIcon, FacilityAdministratorIcon } from '../../core/index.js'
import { DemoSizeBadge, demoSizeLabel } from './DemoSizeBadge.js'
import { OpenAsMenu } from '../../accounts/index.js'
import { DemoSessionsList } from '../../accounts/index.js'
import { ClientContactEditor } from './ClientContactEditor.js'
import { ClientViewAsSwitch } from './ClientViewAsSwitch.js'
import { ClientDataImport } from './ClientDataImport.js'
import { FacilityWorkersModal } from '../../roster/index.js'
import { ClientOperators } from '../../accounts/index.js'
import { ClientKeys } from './ClientKeys.js'
import { ClientScopes } from './ClientScopes.js'
import { ClientAddOns } from '../../add-ons/index.js'
import { AdminClientTextsLine } from '../../messages/index.js'
import { DELETED_LABELS, DeleteDemoClientButton, DeleteDemoClientModal } from './DeleteDemoClient.js'
import { DeleteRegistrationButton, DeleteRegistrationModal } from './DeleteRegistration.js'
import { SortableTh, useSort, type SortAccessors } from '../../core/index.js'
import { Rocket } from 'lucide-react'
import { ButtonGlyph, buttonClass } from '../../core/index.js'
import type { ComponentProps } from 'react'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AccountsAdminApi } from '../../accounts/index.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'
import { LoadFailure, RefreshButton } from '../../core/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = ClientsAdminApi & AccountsAdminApi & FacilitiesAdminApi & ComponentProps<typeof FacilityWorkersModal>['api'] & ComponentProps<typeof ClientAddOns>['api'] & ComponentProps<typeof OpenAsMenu>['api'] & ComponentProps<typeof DemoSessionsList>['api'] & ComponentProps<typeof ClientOperators>['api'] & ComponentProps<typeof AdminClientTextsLine>['api']


const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-lt text-green',
  pending_proof: 'bg-gold-lt text-gold',
  pending_key: 'bg-silver-lt text-silver',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  pending_proof: 'Awaiting key proof',
  pending_key: 'Awaiting key',
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * The at-a-glance size of a client: facilities, and workers across all of
 * them. Numbers only - the icons say what they count, with the wording kept
 * in the tooltip and for screen readers. Anything without a count yet (a
 * registration that isn't active, an empty client, an API that predates
 * these totals) shows a plain 0 rather than a phrase or a blank.
 */
function ClientSummary({ client }: { client: AdminClientSummary }) {
  const facilityCount = client.facilityCount ?? 0
  const workerCount = client.workerCount ?? 0
  const facilityLabel = `${facilityCount.toLocaleString()} ${facilityCount === 1 ? 'facility' : 'facilities'}`
  const workerLabel = `${workerCount.toLocaleString()} ${workerCount === 1 ? 'worker' : 'workers'} across all facilities`

  return (
    <div className="flex items-center gap-4 text-[11px] text-ink-mid whitespace-nowrap">
      <span className="inline-flex items-center gap-1" title={facilityLabel} aria-label={facilityLabel}>
        <FacilityIcon className="w-3.5 h-3.5 text-marine" />
        {facilityCount.toLocaleString()}
      </span>
      <span className="inline-flex items-center gap-1" title={workerLabel} aria-label={workerLabel}>
        <WorkersIcon />
        {workerCount.toLocaleString()}
      </span>
    </div>
  )
}

function contactName(client: AdminClientSummary): string | null {
  return client.contactFirstName && client.contactLastName ? `${client.contactFirstName} ${client.contactLastName}` : null
}

/**
 * Who to reach at the client: their name, with the email and phone number
 * tucked behind icons (hover to read, click to email or call) so the row
 * stays scannable. The full details are also spelled out in the expanded
 * panel. Clicks stop here so using a link doesn't also toggle the row; the
 * pencil opens the row's panel with the contact editor showing.
 */
function ClientContact({ client, onEdit }: { client: AdminClientSummary; onEdit: () => void }) {
  const name = contactName(client)
  // A visible target with a hover state: bare 14px icons in the muted colour read as disabled.
  const iconLink = 'inline-flex items-center justify-center rounded p-1 text-marine-dk hover:bg-marine-lt hover:text-marine-dk cursor-pointer'
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span className={name ? 'text-ink' : 'text-ink-mid'}>{name ?? 'No name on file'}</span>
      <a
        href={`mailto:${client.contactEmail}`}
        title={client.contactEmail}
        aria-label={`Email ${name ?? 'contact'}: ${client.contactEmail}`}
        className={iconLink}
        onClick={(event) => event.stopPropagation()}
      >
        <MailIcon />
      </a>
      {client.contactPhone && (
        <a
          href={`tel:${client.contactPhone.replace(/[^0-9+]/g, '')}`}
          title={client.contactPhone}
          aria-label={`Call ${name ?? 'contact'}: ${client.contactPhone}`}
          className={iconLink}
          onClick={(event) => event.stopPropagation()}
        >
          <PhoneIcon />
        </a>
      )}
      <button
        type="button"
        title="Edit contact"
        aria-label={`Edit contact for ${client.organizationName}`}
        className={iconLink}
        onClick={(event) => {
          event.stopPropagation()
          onEdit()
        }}
      >
        <EditIcon />
      </button>
    </div>
  )
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      <div className="text-xs text-ink font-mono break-all">{value}</div>
    </div>
  )
}

/**
 * A client's facilities, laid out like the operator's own facility list
 * (icon, name, facilityId, roster size) but read-only - there is no admin
 * view of a single facility to link to. Counts follow this view's
 * numbers-only convention, with a plain 0 for an empty roster. Each row
 * also names the facility's administrator(s) - the people who manage it.
 */
function FacilityAdministrators({ administrators, isLoading, isError }: { administrators: FacilityAdministrator[]; isLoading: boolean; isError: boolean }) {
  if (isLoading) {
    return <span className="text-[11px] text-ink-mid">Loading administrator…</span>
  }
  if (isError) {
    return <span className="text-[11px] text-coral">Failed to load administrator.</span>
  }
  if (administrators.length === 0) {
    return <span className="text-[11px] text-ink-mid">No administrator assigned</span>
  }
  return (
    <ul className="space-y-0.5">
      {administrators.map((administrator) => (
        <li key={administrator.administratorId} className="flex items-center justify-end gap-2 text-xs text-ink whitespace-nowrap">
          <FacilityAdministratorIcon className="w-3.5 h-3.5 text-marine" />
          {administrator.firstName} {administrator.lastName}
          <a
            href={`mailto:${administrator.email}`}
            title={administrator.email}
            aria-label={`Email ${administrator.firstName} ${administrator.lastName}: ${administrator.email}`}
            className="text-marine hover:text-marine-dk inline-flex"
          >
            <MailIcon />
          </a>
        </li>
      ))}
    </ul>
  )
}

/**
 * A client's facilities as the operator sees their own list: in name
 * order, found by the start of the name, and loaded a page at a time - a
 * few to begin with, since this is one panel of a client's detail, with a
 * line saying how many of the total are on show.
 */
function ClientFacilities({ api, clientId }: { api: AdminApi; clientId: string }) {
  const [searchText, setSearchText] = useState('')
  const { query: facilitiesQuery, facilities, matching, isSearching } = useClientFacilityPages(api, clientId, useDebouncedValue(searchText))
  const administratorsQuery = useQuery({
    queryKey: ['admin', 'facility-administrators', clientId],
    queryFn: () => api.listFacilityAdministrators(clientId),
  })
  const administrators = administratorsQuery.data ?? []
  const [viewingWorkersOf, setViewingWorkersOf] = useState<AdminFacility | null>(null)

  if (facilitiesQuery.isLoading) {
    return <Skeleton label="Loading facilities" />
  }
  if (facilitiesQuery.isError) {
    return <LoadFailure size="xs" message="Failed to load facilities." onRetry={() => void facilitiesQuery.refetch()} retrying={facilitiesQuery.isFetching} />
  }
  if (facilities.length === 0 && !isSearching) {
    return (
      <div className="border border-marine/15 bg-white px-4 py-4 text-xs text-ink-mid">
        <span className="font-bold text-ink">No facilities set up.</span> Workers, administrators and shifts all belong to a facility, so this client needs one
        first. A facility is the operator's to register, from their own dashboard (New facility); or import a facilities file below.
      </div>
    )
  }
  return (
    <div>
      {(isSearching || matching > FACILITIES_SHOWN_FIRST) && (
        <input
          type="search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Find a facility by the start of its name"
          aria-label="Find a facility"
          autoComplete="off"
          className="mb-2 w-full border border-marine/25 bg-white px-3 py-1.5 text-xs text-ink focus:outline-none focus:border-marine"
        />
      )}
      {facilities.length === 0 && <div className="border border-marine/15 bg-white px-4 py-3 text-xs text-ink-mid">No facility name starts with that.</div>}
    <div className={`border border-marine/15 bg-white divide-y divide-marine/10 ${facilitiesQuery.isPlaceholderData ? 'opacity-60' : ''}`}>
      {facilities.map((facility) => {
        const workerCount = facility.workerCount ?? 0
        const workerLabel = `${workerCount.toLocaleString()} ${workerCount === 1 ? 'worker' : 'workers'} on the roster`
        return (
          <div key={facility.facilityId} className="px-4 py-3 flex items-center gap-3">
            <FacilityIcon className="w-5 h-5 text-marine" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-ink truncate">{facility.name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <button
                  type="button"
                  onClick={() => setViewingWorkersOf(facility)}
                  title={`${workerLabel} - view them`}
                  aria-label={`${workerLabel} at ${facility.name} - view them`}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk"
                >
                  <WorkersIcon />
                  {workerCount.toLocaleString()}
                  <span className="font-bold">· View workers</span>
                </button>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Administrator</div>
              <FacilityAdministrators
                administrators={administrators.filter((administrator) => administrator.facilityIds.includes(facility.facilityId))}
                isLoading={administratorsQuery.isLoading}
                isError={administratorsQuery.isError}
              />
            </div>
          </div>
        )
      })}
      {viewingWorkersOf && <FacilityWorkersModal api={api} facility={viewingWorkersOf} onClose={() => setViewingWorkersOf(null)} />}
    </div>
      {facilities.length > 0 && (
        <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-mid">
          <span>
            Showing {facilities.length.toLocaleString()} of {matching.toLocaleString()} {isSearching ? (matching === 1 ? 'matching facility' : 'matching facilities') : matching === 1 ? 'facility' : 'facilities'}
          </span>
          {facilitiesQuery.hasNextPage && (
            <button type="button" onClick={() => void facilitiesQuery.fetchNextPage()} disabled={facilitiesQuery.isFetchingNextPage} className="font-bold text-marine hover:text-marine-dk disabled:opacity-50">
              {facilitiesQuery.isFetchingNextPage ? 'Loading…' : 'Show more'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Shares the facilities query with ClientFacilities above (same key), so the import can check a roster against them without a second request. */
function ClientImport({ api, clientId }: { api: AdminApi; clientId: string }) {
  const facilitiesQuery = useQuery({
    queryKey: ['admin', 'facilities', clientId],
    queryFn: () => api.listFacilities(clientId),
  })
  return <ClientDataImport api={api} clientId={clientId} facilities={facilitiesQuery.data} />
}

interface ClientDetailProps {
  api: AdminApi
  client: AdminClientSummary
  isEditingContact: boolean
  onEditContact: (isEditing: boolean) => void
}

/** The admin who registered the client: their name while they still have an admin record, else their username; "Not recorded" on a registration older than the field. */
function registeredBy(client: AdminClientSummary): string {
  if (!client.createdBy) {
    return 'Not recorded'
  }
  return client.createdByName ? `${client.createdByName} (${client.createdBy})` : client.createdBy
}

function ClientDetail({ api, client, isEditingContact, onEditContact }: ClientDetailProps) {
  const features = useFeatures()
  const clientId = client.clientId

  return (
    <div className="bg-marine-lt/30 px-4 py-4 grid gap-4 sm:grid-cols-2">
      <DetailField label="Client ID" value={client.clientId ?? 'Not issued yet'} />
      <DetailField label="Registered" value={formatDate(client.createdAt)} />
      <DetailField label="Registered by" value={registeredBy(client)} />
      {client.isDemo && <DetailField label="Demo" value={demoSizeLabel(client.demoSize ?? null)} />}
      {client.isDemo && client.clientId && (
        <div className="sm:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Open this demo as one of its people</div>
          <p className="text-[11px] text-ink-mid mb-2">
            Opens a new tab signed in as that person for an hour, without a password - demo clients only. Nothing in a demo session can change a login.
          </p>
          <OpenAsMenu api={api} clientId={client.clientId} organizationName={client.organizationName} />
          <div className="mt-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">View as for the client's staff</div>
            <ClientViewAsSwitch api={api} client={client} />
          </div>
          <div className="mt-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Demo sessions opened</div>
            <DemoSessionsList api={api} clientId={client.clientId} />
          </div>
        </div>
      )}
      <div className="sm:col-span-2">
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Contact</div>
        {isEditingContact ? (
          <ClientContactEditor api={api} client={client} onDone={() => onEditContact(false)} />
        ) : (
          <div className="text-xs text-ink flex items-center gap-3">
            <span>
              {contactName(client) ?? <span className="text-ink-mid">No name on file</span>}
              <span className="text-ink-mid"> · {client.contactEmail}</span>
              <span className="text-ink-mid"> · {client.contactPhone ?? 'No phone on file'}</span>
            </span>
            <button type="button" onClick={() => onEditContact(true)} className="inline-flex items-center gap-1 font-bold text-marine hover:text-marine-dk">
              <EditIcon />
              Edit
            </button>
          </div>
        )}
      </div>
      <div className="sm:col-span-2">
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Facilities</div>
        {client.clientId ? (
          <ClientFacilities api={api} clientId={client.clientId} />
        ) : (
          <div className="text-xs text-ink-mid">Facilities can be added once the client is active.</div>
        )}
      </div>
      {client.clientId && (
        <div className="sm:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Import data</div>
          <ClientImport api={api} clientId={client.clientId} />
        </div>
      )}
      <div className="sm:col-span-2">
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Operator logins</div>
        <ClientOperators api={api} client={client} />
      </div>
      {client.clientId && (
        <div className="sm:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">API keys</div>
          <ClientKeys api={api} client={client} />
        </div>
      )}
      <div className="sm:col-span-2">
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">API scopes</div>
        <ClientScopes key={client.registrationId} api={api} client={client} />
      </div>
      {client.clientId && (
        <div className="sm:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Add-ons</div>
          <ClientAddOns api={api} clientId={client.clientId} isDemo={client.isDemo === true} />
        </div>
      )}
      {client.clientId && (
        <div className="sm:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Texts</div>
          <AdminClientTextsLine api={api} clientId={client.clientId} />
        </div>
      )}
      {clientId !== null &&
        features.map((feature) =>
          feature.adminClientSection ? (
            <div key={feature.key} className="sm:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">{feature.adminClientSection.heading}</div>
              <feature.adminClientSection.section client={api.client} clientId={clientId} />
            </div>
          ) : null,
        )}
    </div>
  )
}

const CLIENT_SORT: SortAccessors<AdminClientSummary, 'organization' | 'demo' | 'size' | 'status' | 'contact' | 'registered'> = {
  organization: (client) => client.organizationName,
  // Real clients first, then demos by dataset; an older demo with no size on record sorts with the demos.
  demo: (client) => (client.isDemo ? (client.demoSize ?? 'demo') : ''),
  // The summary column shows facility and worker counts; it sorts by roster size.
  size: (client) => client.workerCount ?? 0,
  status: (client) => client.status,
  contact: (client) => client.contactEmail,
  registered: (client) => client.createdAt,
}

/** The counts someone would recognise from setting the client up; the per-worker and per-facility bookkeeping records are left out. */
function describeDeleted(deleted: Record<string, number>): string {
  const parts = DELETED_LABELS.filter(([key]) => (deleted[key] ?? 0) > 0).map(([key, singular, plural]) => {
    const count = deleted[key] ?? 0
    return `${count.toLocaleString()} ${count === 1 ? singular : plural}`
  })
  return parts.length > 0 ? parts.join(', ') : 'the client record (it had no data)'
}

export function ClientList({ api }: { api: AdminApi }) {
  const queryClient = useQueryClient()
  const clientsQuery = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // Which client's contact editor is open - at most one, and only inside its expanded panel.
  const [editingContactId, setEditingContactId] = useState<string | null>(null)

  const clients = [...(clientsQuery.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const { sorted, header } = useSort(clients, CLIENT_SORT)

  // The deleted client's row (and the panel that asked) is gone after the
  // refresh, so the confirmation of what was removed lives up here.
  const [lastDeleted, setLastDeleted] = useState<ClientPurge | null>(null)
  // The demo client whose delete dialog is open. Held here, not in the row: the row is gone once the delete lands, and the dialog stays to show what was removed.
  const [deleting, setDeleting] = useState<AdminClientSummary | null>(null)
  // The unactivated registration whose delete dialog is open: an onboarding that stopped before the key was proved.
  const [deletingRegistration, setDeletingRegistration] = useState<AdminClientSummary | null>(null)
  async function handleRegistrationDeleted(): Promise<void> {
    setExpandedId(null)
    await queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
  }
  async function handleDeleted(result: ClientPurge): Promise<void> {
    setLastDeleted(result)
    setExpandedId(null)
    // Drop rather than refetch what was cached for the deleted client - those requests would only 404.
    queryClient.removeQueries({ queryKey: ['admin', 'facilities', result.clientId] })
    queryClient.removeQueries({ queryKey: ['admin', 'facility-administrators', result.clientId] })
    await queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
  }

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      {deleting?.clientId && (
        <DeleteDemoClientModal key={deleting.registrationId} api={api} client={deleting} clientId={deleting.clientId} onDeleted={(result) => void handleDeleted(result)} onClose={() => setDeleting(null)} />
      )}
      {deletingRegistration && (
        <DeleteRegistrationModal key={deletingRegistration.registrationId} api={api} client={deletingRegistration} onDeleted={() => void handleRegistrationDeleted()} onClose={() => setDeletingRegistration(null)} />
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-ink-mid">
          {clientsQuery.isSuccess && `${clients.length} ${clients.length === 1 ? 'client' : 'clients'}`}
        </div>
        {/* Onboarding takes the admin out of this list; remembering it gives the shell its Back button and brings them back to the list as they left it. */}
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={() => clientsQuery.refetch()} refreshing={clientsQuery.isFetching} />
          <Link to="/admin/onboard" rememberOrigin className={buttonClass()}>
            <ButtonGlyph icon={Rocket} />
            Onboard a client
          </Link>
        </div>
      </div>

      {lastDeleted && (
        <div className="border border-marine/15 bg-marine-lt/40 px-4 py-3 mb-4 text-xs text-ink flex items-start justify-between gap-4">
          <div>
            <span className="font-bold">Deleted demo client {lastDeleted.organizationName}.</span> Removed {describeDeleted(lastDeleted.deleted)}.
          </div>
          <button type="button" onClick={() => setLastDeleted(null)} className="text-[11px] font-bold text-ink-mid hover:text-ink shrink-0">
            Dismiss
          </button>
        </div>
      )}

      {clientsQuery.isLoading ? (
        <Skeleton lines={5} label="Loading clients" />
      ) : clientsQuery.isError ? (
        <LoadFailure message="Failed to load clients. Confirm the API is reachable." onRetry={() => void clientsQuery.refetch()} retrying={clientsQuery.isFetching} />
      ) : clients.length === 0 ? (
        <div className="bg-white border border-marine/15 p-8 text-sm text-ink-mid">
          No clients have been registered yet. Onboard the first one to see it here.
        </div>
      ) : (
        <div className="bg-white border border-marine/15 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
                <SortableTh className="px-4 py-2" label="Organization" {...header('organization')} />
                <SortableTh className="px-4 py-2" label="Demo" {...header('demo')} />
                <th scope="col" className="px-4 py-2 font-bold">Open as</th>
                <SortableTh className="px-4 py-2" label="Summary" {...header('size')} />
                <SortableTh className="px-4 py-2" label="Status" {...header('status')} />
                <SortableTh className="px-4 py-2" label="Contact" {...header('contact')} />
                <SortableTh className="px-4 py-2" label="Registered" {...header('registered')} />
                <th scope="col" className="px-4 py-2">
                  <span className="sr-only">Delete</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((client) => {
                const isExpanded = expandedId === client.registrationId
                return (
                  <Fragment key={client.registrationId}>
                    <tr
                      className="border-b border-marine/10 cursor-pointer hover:bg-marine-lt/20"
                      onClick={() => {
                        setExpandedId(isExpanded ? null : client.registrationId)
                        setEditingContactId(null)
                      }}
                    >
                      <td className="px-4 py-3 font-bold text-ink">
                        <button type="button" aria-expanded={isExpanded} className="text-left font-bold">
                          <span aria-hidden="true" className="inline-block w-3 text-ink-mid">
                            {isExpanded ? '▾' : '▸'}
                          </span>{' '}
                          {client.organizationName}
                        </button>
                      </td>
                      <td className="px-4 py-3">{client.isDemo && <DemoSizeBadge size={client.demoSize ?? null} />}</td>
                      <td className="px-4 py-3">
                        {client.isDemo && client.clientId && <OpenAsMenu api={api} clientId={client.clientId} organizationName={client.organizationName} compact />}
                      </td>
                      <td className="px-4 py-3">
                        <ClientSummary client={client} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLES[client.status] ?? 'bg-silver-lt text-silver'}`}>
                          {STATUS_LABELS[client.status] ?? client.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ClientContact
                          client={client}
                          onEdit={() => {
                            setExpandedId(client.registrationId)
                            setEditingContactId(client.registrationId)
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 text-ink-mid whitespace-nowrap">
                        {formatDate(client.createdAt)}
                        {client.createdBy && (
                          <div className="text-[10px] text-ink-mid/80" title={client.createdBy}>
                            by {client.createdByName ?? client.createdBy}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {client.isDemo && client.clientId && <DeleteDemoClientButton client={client} onClick={() => setDeleting(client)} />}
                        {!client.clientId && <DeleteRegistrationButton client={client} onClick={() => setDeletingRegistration(client)} />}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-marine/10">
                        <td colSpan={8} className="p-0">
                          <ClientDetail
                            api={api}
                            client={client}
                            isEditingContact={editingContactId === client.registrationId}
                            onEditContact={(isEditing) => setEditingContactId(isEditing ? client.registrationId : null)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
